import { useState } from 'react';

import { FileSpreadsheet, FileText, Gavel, Receipt, type LucideIcon } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '@/nucleo/auth/useAuth';
import { formatarData } from '@/nucleo/formato/datas';
import { cn } from '@/nucleo/utilitarios';
import { ClearFiltersButton } from '@/ui/ClearFiltersButton';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { ErrorState } from '@/ui/ErrorState';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { MonthFilter } from '@/ui/MonthFilter';
import { PageHeader } from '@/ui/PageHeader';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent } from '@/ui/base/card';

import { useLancamentosDoExtrato } from './dados';
import { exportarExtratoParaPdf, exportarExtratoParaXlsx } from './exportar';
import {
  TAMANHO_PAGINA_PADRAO,
  type FiltroDoExtrato,
  type LancamentoDoExtrato,
  type TipoDeLancamento,
} from './contrato';

/**
 * `/credenciado/extrato` — extrato consolidado do credenciado.
 *
 * Uma lista única combinando lançamentos de medições, demonstrativos e
 * multas, em ordem cronológica, cada um com o efeito no saldo acumulado.
 * Dado mockado — ver `dados.ts` e `comum/mock.ts` para a justificativa.
 */

const TIPO_DO_LANCAMENTO: Record<TipoDeLancamento, { rotulo: string; icone: LucideIcon }> = {
  medicao: { rotulo: 'Medição', icone: Receipt },
  demonstrativo: { rotulo: 'Demonstrativo', icone: FileText },
  multa: { rotulo: 'Multa', icone: Gavel },
};

export function ExtratoConsolidadoPage() {
  const { usuario } = useAuth();
  const [competencia, setCompetencia] = useState('');
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState(TAMANHO_PAGINA_PADRAO);

  const filtro: FiltroDoExtrato = { pagina, tamanho, competencia: competencia || undefined };
  const { data, isLoading, isError, refetch } = useLancamentosDoExtrato(filtro);

  const nomeDoCredenciado = usuario?.nome ?? 'Credenciado';

  // O arquivo leva as linhas que a consulta devolveu e a tela mostra, e nenhuma
  // outra: exportar de uma segunda leitura da fonte deixaria o arquivo
  // divergir do que o usuário viu.
  const exportar = (formato: 'xlsx' | 'pdf') => {
    const todos = data?.itens ?? [];
    if (todos.length === 0) {
      toast.error('Sem dados para exportar');
      return;
    }
    if (formato === 'xlsx') {
      exportarExtratoParaXlsx(todos, nomeDoCredenciado);
      toast('Planilha gerada');
    } else {
      exportarExtratoParaPdf(todos, nomeDoCredenciado);
      toast('PDF gerado');
    }
  };

  const colunas: DataTableColumn<LancamentoDoExtrato>[] = [
    {
      id: 'data',
      header: 'Data',
      cell: (lancamento) => <span className="text-sm">{formatarData(lancamento.data)}</span>,
      sortKey: 'data',
    },
    {
      id: 'tipo',
      header: 'Tipo',
      cell: (lancamento) => {
        const { rotulo, icone: Icone } = TIPO_DO_LANCAMENTO[lancamento.tipo];
        return (
          <Badge variant="outline" className="gap-1 font-normal">
            <Icone className="h-3 w-3" aria-hidden="true" />
            {rotulo}
          </Badge>
        );
      },
      sortKey: 'tipo',
    },
    {
      id: 'descricao',
      header: 'Descrição',
      cell: (lancamento) => <span className="text-sm">{lancamento.descricao}</span>,
    },
    {
      id: 'valor',
      header: 'Valor',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (lancamento) => (
        <MoneyDisplay
          value={lancamento.valor}
          className={cn(lancamento.valor < 0 ? 'text-destructive-text' : 'text-success-text')}
        />
      ),
      sortKey: 'valor',
    },
    {
      id: 'saldo_acumulado',
      header: 'Saldo acumulado',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (lancamento) => (
        <MoneyDisplay value={lancamento.saldo_acumulado} className="font-semibold" />
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Extrato Consolidado"
        description="Todos os lançamentos financeiros do seu credenciamento, em um único extrato."
      />

      {isError ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar o extrato agora. Tente novamente em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : (
        <>
          <Card>
            <CardContent className="flex flex-col items-stretch gap-3 p-4 sm:flex-row sm:items-end">
              <MonthFilter
                value={competencia}
                onChange={(valor) => {
                  setCompetencia(valor);
                  setPagina(1);
                }}
              />
              <ClearFiltersButton
                active={!!competencia}
                onClear={() => {
                  setCompetencia('');
                  setPagina(1);
                }}
                className="self-end"
              />
              <div className="flex flex-wrap gap-2 sm:ml-auto">
                <Button variant="outline" onClick={() => exportar('xlsx')} disabled={isLoading}>
                  <FileSpreadsheet className="mr-2 h-4 w-4" aria-hidden="true" />
                  Baixar planilha (XLSX)
                </Button>
                <Button onClick={() => exportar('pdf')} disabled={isLoading}>
                  <FileText className="mr-2 h-4 w-4" aria-hidden="true" />
                  Baixar PDF
                </Button>
              </div>
            </CardContent>
          </Card>

          <p className="text-muted-foreground text-xs">
            A planilha e o PDF levam as linhas que estão na tela. Para exportar mais linhas de uma
            vez, aumente a quantidade por página.
          </p>

          <DataTable
            columns={colunas}
            data={data?.itens ?? []}
            isLoading={isLoading}
            total={data?.total}
            page={pagina}
            perPage={tamanho}
            onPageChange={setPagina}
            onPerPageChange={(novoTamanho) => {
              setTamanho(novoTamanho);
              setPagina(1);
            }}
            emptyIcon={Receipt}
            emptyTitle="Nenhum lançamento encontrado"
            emptyDescription={
              competencia
                ? 'Nenhum lançamento nesse mês. Tente outra competência.'
                : 'Quando houver lançamento financeiro, ele aparece aqui.'
            }
            minWidth="720px"
            ariaLabel="Extrato consolidado de lançamentos"
          />
        </>
      )}
    </div>
  );
}

export default ExtratoConsolidadoPage;
